/**
 * Agent Memory Browser Component
 * Advanced memory filtering, searching, and management
 */

import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MemoryIcon from '@mui/icons-material/Memory';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/FilterList';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpiredIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';

import { agentApi } from '../../services/api';
import { LoadingSpinner } from '../common';
import type { AgentMemory, AgentMemoryCreate, AgentMemoryUpdate } from '../../types';

interface AgentMemoryBrowserProps {
  agentId: number;
}

export const AgentMemoryBrowser: React.FC<AgentMemoryBrowserProps> = ({ agentId }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [memoryTypeFilter, setMemoryTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [scopeFilter, setScopeFilter] = useState<'private' | 'shared' | 'published'>('private');
  const [importanceRange, setImportanceRange] = useState<number[]>([0, 1]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState<AgentMemory | null>(null);

  // Form state
  const [createFormData, setCreateFormData] = useState<AgentMemoryCreate>({
    agent_id: agentId,
    memory_type: 'LONG_TERM',
    content: '',
    importance_score: 0.5,
    write_scope: 'self',
  });
  
  const [editFormData, setEditFormData] = useState<AgentMemoryUpdate>({
    content: '',
    importance_score: 0.5,
  });

  const normalizedSearchTerm = searchTerm.trim();
  const readScopes =
    scopeFilter === 'published'
      ? ['published']
      : scopeFilter === 'shared'
        ? ['self', 'published']
        : ['self'];
  const usesScopedSearch =
    scopeFilter !== 'private' ||
    normalizedSearchTerm.length > 0 ||
    memoryTypeFilter !== 'all' ||
    importanceRange[0] > 0;

  useEffect(() => {
    setPage(0);
  }, [normalizedSearchTerm, memoryTypeFilter, statusFilter, scopeFilter, importanceRange[0], importanceRange[1]]);

  // Fetch memories
  const { data: memoriesData, isLoading, error, refetch } = useQuery({
    queryKey: [
      'agent-memories',
      agentId,
      page,
      rowsPerPage,
      normalizedSearchTerm,
      memoryTypeFilter,
      scopeFilter,
      importanceRange[0],
    ],
    queryFn: () => (
      usesScopedSearch
        ? agentApi.searchMemories({
            agent_id: agentId,
            query: normalizedSearchTerm || undefined,
            memory_type: memoryTypeFilter !== 'all' ? (memoryTypeFilter as AgentMemory['memory_type']) : undefined,
            min_importance: importanceRange[0] > 0 ? importanceRange[0] : undefined,
            read_scopes: readScopes,
            limit: Math.max(200, (page + 1) * rowsPerPage),
          })
        : agentApi.getMemories(agentId, page + 1, rowsPerPage)
    ),
    enabled: !!agentId,
  });

  // Fetch memory stats
  const { data: memoryStats } = useQuery({
    queryKey: ['agent-memory-stats', agentId, scopeFilter],
    queryFn: () => agentApi.getMemoryStats(agentId, { read_scopes: readScopes }),
    enabled: !!agentId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentMemoryCreate) => agentApi.createMemory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-memories', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-memory-stats', agentId] });
      toast.success('Memory created successfully');
      handleCloseCreateDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create memory');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentMemoryUpdate }) =>
      agentApi.updateMemory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-memories', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-memory-stats', agentId] });
      toast.success('Memory updated successfully');
      handleCloseEditDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update memory');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentApi.deleteMemory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-memories', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-memory-stats', agentId] });
      toast.success('Memory deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete memory');
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => agentApi.publishMemory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-memories', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent-memory-stats', agentId] });
      toast.success('Memory published to shared scope');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to publish memory');
    },
  });

  const handleOpenCreateDialog = () => {
    setCreateFormData({
      agent_id: agentId,
      memory_type: 'LONG_TERM',
      content: '',
      importance_score: 0.5,
      write_scope: 'self',
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateFormData({
      agent_id: agentId,
      memory_type: 'LONG_TERM',
      content: '',
      importance_score: 0.5,
      write_scope: 'self',
    });
  };

  const handleCreateMemory = () => {
    createMutation.mutate(createFormData);
  };

  const handleOpenEditDialog = (memory: AgentMemory) => {
    setSelectedMemory(memory);
    setEditFormData({
      content: memory.content,
      importance_score: memory.importance_score,
      metadata: memory.metadata,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedMemory(null);
    setEditFormData({
      content: '',
      importance_score: 0.5,
    });
  };

  const handleUpdateMemory = () => {
    if (!selectedMemory) return;
    updateMutation.mutate({
      id: selectedMemory.id,
      data: editFormData,
    });
  };

  const handleViewMemory = (memory: AgentMemory) => {
    setSelectedMemory(memory);
    setViewDialogOpen(true);
  };

  const handleDeleteMemory = (memory: AgentMemory) => {
    if (window.confirm(`Are you sure you want to delete this memory?`)) {
      deleteMutation.mutate(memory.id);
    }
  };

  const handlePublishMemory = (memory: AgentMemory) => {
    if (memory.scope_visibility === 'published') {
      return;
    }

    if (window.confirm('Publish this memory to the shared agent-type namespace?')) {
      publishMutation.mutate(memory.id);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMemoryTypeColor = (type: string) => {
    switch (type) {
      case 'SHORT_TERM':
        return 'info';
      case 'LONG_TERM':
        return 'success';
      case 'SEMANTIC':
        return 'primary';
      case 'EPISODIC':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getScopeLabel = (memory: AgentMemory) =>
    memory.scope_visibility === 'published' ? 'Published' : 'Private';

  const getScopeColor = (memory: AgentMemory) =>
    memory.scope_visibility === 'published' ? 'secondary' : 'default';

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load memories. Please try again later.
      </Alert>
    );
  }

  if (!memoriesData || !memoriesData.results) {
    return (
      <Alert severity="info">
        No memory data available.
      </Alert>
    );
  }

  // Filter memories (safe to access memoriesData here)
  const filteredMemories = memoriesData.results.filter((memory) => {
    const matchesSearch = usesScopedSearch
      ? true
      : memory.content.toLowerCase().includes(normalizedSearchTerm.toLowerCase());
    const matchesType = usesScopedSearch || memoryTypeFilter === 'all'
      ? true
      : memory.memory_type === memoryTypeFilter;
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'active' && !memory.is_expired) ||
      (statusFilter === 'expired' && memory.is_expired);
    const matchesImportance =
      (usesScopedSearch || memory.importance_score >= importanceRange[0]) &&
      memory.importance_score <= importanceRange[1];
    const matchesScope =
      scopeFilter === 'shared' ||
      (scopeFilter === 'published' && memory.scope_visibility === 'published') ||
      (scopeFilter === 'private' && memory.scope_visibility !== 'published');

    return matchesSearch && matchesType && matchesStatus && matchesImportance && matchesScope;
  });

  const pagedMemories = usesScopedSearch
    ? filteredMemories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredMemories;
  const memoryCount = usesScopedSearch ? filteredMemories.length : (memoriesData?.count || 0);

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4">{memoryStats?.total || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Memories</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4">
                {memoryStats?.avg_importance?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Importance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4">{memoryStats?.expired || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Expired</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h4">
                {memoryStats?.active || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Memory Type Breakdown */}
      {memoryStats?.by_type && Object.keys(memoryStats.by_type).length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(memoryStats.by_type).map(([type, count]) => (
            <Grid size={{ xs: 6, sm: 3 }} key={type}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h5">{count}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type.replace('_', ' ')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon /> Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Create Memory
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={memoryTypeFilter}
                label="Type"
                onChange={(e) => setMemoryTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="SHORT_TERM">Short Term</MenuItem>
                <MenuItem value="LONG_TERM">Long Term</MenuItem>
                <MenuItem value="SEMANTIC">Semantic</MenuItem>
                <MenuItem value="EPISODIC">Episodic</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Scope</InputLabel>
              <Select
                value={scopeFilter}
                label="Scope"
                onChange={(e) => setScopeFilter(e.target.value as 'private' | 'shared' | 'published')}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="shared">Private + Published</MenuItem>
                <MenuItem value="published">Published Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ px: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Importance: {importanceRange[0].toFixed(2)} - {importanceRange[1].toFixed(2)}
              </Typography>
              <Slider
                value={importanceRange}
                onChange={(_e, newValue) => setImportanceRange(newValue as number[])}
                valueLabelDisplay="auto"
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' },
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Memory Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Importance</TableCell>
                <TableCell>Access Count</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedMemories.length > 0 ? (
                pagedMemories.map((memory) => (
                  <TableRow key={memory.id}>
                    <TableCell>
                      <Chip
                        label={memory.memory_type.replace('_', ' ')}
                        size="small"
                        color={getMemoryTypeColor(memory.memory_type) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getScopeLabel(memory)}
                        size="small"
                        color={getScopeColor(memory) as any}
                        variant={memory.scope_visibility === 'published' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ maxWidth: 400, cursor: 'pointer' }}
                        onClick={() => handleViewMemory(memory)}
                      >
                        {memory.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {memory.importance_score.toFixed(2)}
                        </Typography>
                        <Box
                          sx={{
                            width: 50,
                            height: 4,
                            bgcolor: 'grey.300',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${memory.importance_score * 100}%`,
                              height: '100%',
                              bgcolor: memory.importance_score >= 0.7 ? 'success.main' : 'warning.main',
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{memory.access_count}</TableCell>
                    <TableCell>
                      {new Date(memory.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={memory.is_expired ? 'Expired' : 'Active'}
                        color={memory.is_expired ? 'default' : 'success'}
                        size="small"
                        icon={memory.is_expired ? <ExpiredIcon /> : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {memory.scope_visibility !== 'published' && (
                        <Tooltip title="Publish to shared memory">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handlePublishMemory(memory)}
                            disabled={publishMutation.isPending}
                          >
                            <PublishIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(memory)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteMemory(memory)}
                        >
                          <DeleteIcon />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 4 }}>
                      <MemoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        No memories found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={memoryCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create Memory Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create New Memory</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Memory Type</InputLabel>
              <Select
                value={createFormData.memory_type}
                label="Memory Type"
                onChange={(e) => setCreateFormData({ ...createFormData, memory_type: e.target.value as any })}
              >
                <MenuItem value="SHORT_TERM">Short Term</MenuItem>
                <MenuItem value="LONG_TERM">Long Term</MenuItem>
                <MenuItem value="SEMANTIC">Semantic</MenuItem>
                <MenuItem value="EPISODIC">Episodic</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                value={createFormData.write_scope || 'self'}
                label="Scope"
                onChange={(e) => setCreateFormData({ ...createFormData, write_scope: e.target.value })}
              >
                <MenuItem value="self">Private</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Content"
              value={createFormData.content}
              onChange={(e) => setCreateFormData({ ...createFormData, content: e.target.value })}
              required
            />

            <Box>
              <Typography gutterBottom>
                Importance Score: {createFormData.importance_score?.toFixed(2)}
              </Typography>
              <Slider
                value={createFormData.importance_score || 0.5}
                onChange={(_e, value) => setCreateFormData({ ...createFormData, importance_score: value as number })}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: 'Low' },
                  { value: 0.5, label: 'Medium' },
                  { value: 1, label: 'High' },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateMemory}
            disabled={!createFormData.content || createMutation.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Memory Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Memory</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Content"
              value={editFormData.content}
              onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
              required
            />

            <Box>
              <Typography gutterBottom>
                Importance Score: {editFormData.importance_score?.toFixed(2)}
              </Typography>
              <Slider
                value={editFormData.importance_score || 0.5}
                onChange={(_e, value) => setEditFormData({ ...editFormData, importance_score: value as number })}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: 'Low' },
                  { value: 0.5, label: 'Medium' },
                  { value: 1, label: 'High' },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateMemory}
            disabled={!editFormData.content || updateMutation.isPending}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Memory Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Memory Details</DialogTitle>
        <DialogContent>
          {selectedMemory && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip
                  label={selectedMemory.memory_type.replace('_', ' ')}
                  color={getMemoryTypeColor(selectedMemory.memory_type) as any}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Scope</Typography>
                <Chip
                  label={getScopeLabel(selectedMemory)}
                  color={getScopeColor(selectedMemory) as any}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Content</Typography>
                <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {selectedMemory.content}
                </Typography>
              </Box>

              <Divider />

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Importance</Typography>
                  <Typography variant="h6">{selectedMemory.importance_score.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Access Count</Typography>
                  <Typography variant="h6">{selectedMemory.access_count}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">
                    {new Date(selectedMemory.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Last Accessed</Typography>
                  <Typography variant="body2">
                    {new Date(selectedMemory.last_accessed).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">Scope Namespace</Typography>
                  <Typography variant="body2">{selectedMemory.scope_namespace || 'agent-local'}</Typography>
                </Grid>
                {selectedMemory.source_session_id && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">Source Session</Typography>
                    <Typography variant="body2">{selectedMemory.source_session_id}</Typography>
                  </Grid>
                )}
              </Grid>

              {selectedMemory.metadata && Object.keys(selectedMemory.metadata).length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Metadata
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedMemory.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentMemoryBrowser;
